import { Button, ExternalLink, Input, PageModal, Text, usePageModal } from '@ui/themed';
import { t } from 'i18next';
import { useChecklistItemCtx } from '../../../../../components/checklist';
import { useHandlers } from '../../../../../context/page-context';
import { useSettings } from '../../../../../context/settings-context';
import {
  PriceListCreateForm,
  type PriceListTemplate,
  templateToInitialState,
  usePriceListTemplates,
} from '../../../../price-lists';
import { StepWithPreview } from '../../../shared/components/step-with-preview';
import type { OnboardingChecklistStep } from '../../../shared/types';
import { PREVIEW_PLACEHOLDER_DISCOUNT } from './constants';
import { PriceGraphic } from './price-graphic';
import { parsePreviewDiscount } from './schemas';
import { useChecklistStatus } from './use-checklist-status';
import { useDiscountPriceListForm } from './use-discount-price-list-form';

export const PRICE_LIST_STEP: OnboardingChecklistStep = {
  icon: 'priceList',
  id: 'price-list',
  title: t('features:onboarding.components.price_list_item.title'),
  useStatus: useChecklistStatus,
  children: <CreatePriceListBody />,
};

const PRICE_LIST_DOCS_URL = 'https://docs.sparklayer.io/price-lists';

function CreatePriceListBody() {
  const { status } = useChecklistItemCtx();
  return status === 'complete' ? <CreatePriceListComplete /> : <CreatePriceListPending />;
}

function CreatePriceListPending() {
  const { baseCurrency } = useSettings();
  const [template] = usePriceListTemplates();
  const form = useDiscountPriceListForm(template);

  const preview = (
    <form.Subscribe selector={(state) => state.values.discount}>
      {(discount) => (
        <PriceGraphic
          currencyCode={baseCurrency}
          discountPercent={parsePreviewDiscount(discount) ?? PREVIEW_PLACEHOLDER_DISCOUNT}
        />
      )}
    </form.Subscribe>
  );

  return (
    <StepWithPreview preview={preview}>
      <PageModal>
        <Text>{t('features:onboarding.components.price_list_item.description')}</Text>
        <form
          className="flex items-start gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            form.handleSubmit();
          }}
        >
          <div className="flex flex-row flex-wrap items-center gap-x-2 gap-y-2">
            <form.Field name="discount">
              {(field) => (
                // Input error is validation-only; a create failure is reported via a toast instead.
                <Input
                  suffix="%"
                  value={field.state.value}
                  onChange={field.handleChange}
                  inputMode="decimal"
                  short
                  error={
                    field.state.meta.errors.length > 0
                      ? t('features:onboarding.components.price_list_item.error_invalid_amount')
                      : undefined
                  }
                />
              )}
            </form.Field>
            <form.Subscribe selector={(state) => [state.canSubmit, state.isSubmitting]}>
              {([canSubmit, isSubmitting]) => (
                <Button type="submit" loading={isSubmitting} disabled={!canSubmit}>
                  {t('features:onboarding.components.price_list_item.submit')}
                </Button>
              )}
            </form.Subscribe>
            <PageModal.Trigger asChild>
              <Button type="button" variant="secondary">
                {t('features:onboarding.components.price_list_item.setup_myself')}
              </Button>
            </PageModal.Trigger>
          </div>
        </form>
        <ManualSetupModal template={template} />
      </PageModal>
    </StepWithPreview>
  );
}

function ManualSetupModal({ template }: { template: PriceListTemplate | undefined }) {
  const { close } = usePageModal();
  return (
    <PageModal.Content title={t('price_list_builder.header.title.create')}>
      <PriceListCreateForm
        draftState={template ? templateToInitialState(template) : undefined}
        onSuccess={close}
        onCancel={close}
      />
    </PageModal.Content>
  );
}

function CreatePriceListComplete() {
  const { baseCurrency } = useSettings();
  const { navigationHandler } = useHandlers();

  const preview = (
    <PriceGraphic currencyCode={baseCurrency} discountPercent={PREVIEW_PLACEHOLDER_DISCOUNT} />
  );

  return (
    <StepWithPreview preview={preview}>
      <Text>
        {t('features:onboarding.components.price_list_item.complete')}{' '}
        <ExternalLink link={PRICE_LIST_DOCS_URL} showIcon>
          {t('features:onboarding.components.price_list_item.learn_more')}
        </ExternalLink>
      </Text>
      <div>
        <Button
          variant="secondary"
          icon="edit"
          onClick={() => navigationHandler.navigate('/pricing/lists')}
        >
          {t('features:onboarding.components.price_list_item.manage')}
        </Button>
      </div>
    </StepWithPreview>
  );
}
